import React, { PureComponent, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router-dom";
import { useSwipeable } from "react-swipeable";

import client from "@culturehq/client";
import { Button, classnames, Icon, ModalDialog, PlainButton, Tooltip } from "@culturehq/components";

import { useProfile } from "app/context";
import { errorService, isMultiImageStory, mobileAndTabletCheck, t, Trans } from "app/utils";

import ApproveButton from "components/admin/settings/stories/ApproveButton";
import ApproveLandingPageButton from "components/admin/settings/stories/ApproveLandingPageButton";
import ShareStoryOptions from "components/admin/settings/ShareStoryOptions";
import KeepAsInternalButton from "components/admin/settings/stories/KeepAsInternalButton";
import ApproveForJobMappingButton from "components/admin/settings/stories/ApproveForJobMappingButton";
import JobPostingMappingModal from "components/admin/settings/stories/job-posting-modal/JobPostingMappingModal";
import PhotoTagging from "./tags/PhotoTagging";
import ShareLinkComponent from "../ShareLinkComponent";
import LightboxStoryPhoto from "./LightboxStoryPhoto";
import IconsPagination from "../IconsPagination";
import StoryShareFacebookInstagram from "../StoryShareFacebookInstagram";
import ChqModalDialog from "../ChqModalDialog";

const QuickOptionsSelector = ({
  approvedContent, landingPages, story, onUpdateApprovedForSocial
}) => {
  const { profile } = useProfile();
  const [qrToshare, setQrToshare] = useState([]);

  const onShareToQRLink = values => {
    if (values.length > 0) {
      setQrToshare(values);
    } else {
      setQrToshare([]);
    }
  };

  const handleQRClose = () => {
    setQrToshare([]);
  };

  const isJobMappingDisabled = !profile.organization.externalMappedIds
    || !profile.organization.externalMappers
    || profile.organization.externalMappers.length === 0;

  return (
    <>
      {qrToshare.length > 0 && (
        <StoryShareFacebookInstagram
          story={story}
          initialOpen
          onClose={handleQRClose}
          qrToshare={qrToshare}
        />
      )}
      {isJobMappingDisabled ? (
        <div className="ApproveJobMappingButton__Tooltip-Container">
          <Tooltip tip={t("You haven't set up job mapper values for your organization")}>
            <ApproveForJobMappingButton
              approvedForSocial={approvedContent}
              onUpdate={onUpdateApprovedForSocial}
              hideTooltip
            />
          </Tooltip>
        </div>
      ) : (
        <ApproveForJobMappingButton
          approvedForSocial={approvedContent}
          onUpdate={onUpdateApprovedForSocial}
          hideTooltip
        />
      )}
      {profile.activeLandingPage && (
        <ApproveLandingPageButton
          approvedContent={approvedContent}
          externalState={story.externalState}
          landingPages={landingPages}
          onUpdateApprovedForSocial={onUpdateApprovedForSocial}
        />
      )}
      <ShareStoryOptions
        approvedContent={approvedContent}
        className="Approved-actions__Share"
        story={story}
        onShareToQRLink={onShareToQRLink}
      />
    </>
  );
};

const LightboxArrows = ({ onSwipeLeft, onSwipeRight }) => (
  <>
    <button aria-label={t("Go to previous")} type="button" className="gallery-lightbox__chevron-left" onClick={onSwipeLeft}>
      <Icon icon="chevron-left" />
    </button>
    <button aria-label={t("Go to next")} type="button" className="gallery-lightbox__chevron-right" onClick={onSwipeRight}>
      <Icon icon="chevron-right" />
    </button>
  </>
);

const AdminLightboxActions = ({
  activeStory,
  approvedContent,
  approvedVersion,
  handleApproveClicked,
  handleKeepAsInternalClicked,
  internalVersion,
  landingPages,
  mappedVersion,
  onPromptChange,
  onClose,
  onUpdateApprovedForSocial,
  onSwipeLeft,
  onSwipeRight,
  reviewVersion,
  stories,
  onUpdateMappings
}) => (
  <div className="row">
    <div className="col-xs-12">
      <div className="gallery-lightbox__full-header">
        <div className="title">
          {approvedVersion ? (
            <>
              <Icon icon="share" />
              <h4><Trans>Publish Stories</Trans></h4>
            </>
          ) : (
            <>
              <Icon icon="checkmark" />
              <h4><Trans>Review Stories</Trans></h4>
            </>
          )}
        </div>
        <div className="navigation">
          <PlainButton className="left-arrow" aria-label={t("Go to previous")} onClick={onSwipeLeft}>
            <Icon icon="chevron-left" />
          </PlainButton>
          <IconsPagination
            currentPage={stories.findIndex(story => story.id === activeStory.id) + 1}
            onClick={onPromptChange}
            totalPages={stories.length}
            smallVersion
          />
          <PlainButton className="right-arrow" aria-label={t("Go to next")} onClick={onSwipeRight}>
            <Icon icon="chevron-right" />
          </PlainButton>
        </div>
        <div className="actions">
          {internalVersion && activeStory.externalState !== "archived" && (
            <ApproveButton
              story={activeStory}
              onClick={handleApproveClicked}
              displayConfirm={false}
            />
          )}

          {approvedVersion && (
            <QuickOptionsSelector
              approvedContent={approvedContent}
              landingPages={landingPages}
              story={activeStory}
              onUpdateApprovedForSocial={onUpdateApprovedForSocial}
            />
          )}
          {reviewVersion && (
            <>
              <KeepAsInternalButton
                story={activeStory}
                onClick={handleKeepAsInternalClicked}
              />
              <ApproveButton story={activeStory} onClick={handleApproveClicked} displayConfirm />
            </>
          )}
          {mappedVersion && (
            <JobPostingMappingModal
              editImmediately
              story={activeStory}
              onUpdate={onUpdateMappings}
            >
              {onOpen => (
                <Button disabled={!onOpen} onClick={onOpen}>
                  <Icon icon="edit" /> Job Mappings
                </Button>
              )}
            </JobPostingMappingModal>
          )}
          <PlainButton className="close-btn" aria-label={t("Close")} onClick={onClose}>
            <Icon icon="close" />
          </PlainButton>
        </div>
      </div>
    </div>
  </div>
);

const LightboxActions = ({
  activeStory,
  hideShareLink = false,
  insideIframe,
  onClose,
  landingPage,
  landingPagePrefix,
  organization,
  organizationId,
  preview,
  onSwipeLeft,
  onSwipeRight,
  orgName
}) => {
  const link = useMemo(() => {
    if (!organization) return "";

    const getDomain = () => {
      if (process.env.NODE_ENV === "production") return "culturehq.com";
      return "localhost:4000";
    };

    const domain = getDomain();
    const link = `${organization.replace(/\s+/g, "-").toLowerCase()}.${domain}${landingPagePrefix ? `/${landingPagePrefix}` : ""}`;

    if (process.env.NODE_ENV === "production") return `https://${link}`;
    return `http://${link}`;
  }, [landingPagePrefix, organization]);

  return (
    <div className="gallery-lightbox--actions">
      <div className="navigation">
        <PlainButton className="left-arrow" aria-label={t("Go to previous")} onClick={onSwipeLeft}>
          <Icon icon="chevron-left" />
        </PlainButton>
        <PlainButton className="right-arrow" aria-label={t("Go to next")} onClick={onSwipeRight}>
          <Icon icon="chevron-right" />
        </PlainButton>
      </div>
      <div className="actions">
        {landingPage && !preview && !hideShareLink && !insideIframe && (
          <ShareLinkComponent
            link={`${link}/story/${activeStory.id}`}
            modal
            orgName={orgName}
            organizationId={organizationId}
            storyId={activeStory.id}
          />
        )}
        <PlainButton aria-label={t("Close")} onClick={onClose}>
          <Icon icon="close" />
        </PlainButton>
      </div>
    </div>
  );
};

class LightboxStoriesWrapper extends PureComponent {
  containerRef = React.createRef();

  timeout = 0;

  static defaultProps = {
    onStoryChange: () => {}
  };

  state = {
    tagging: false,
    taggingPosition: null,
    anotherModalOpen: false
  };

  componentDidUpdate(prevProps) {
    const { activeStory, modalIsOpen } = this.props;

    if (modalIsOpen) {
      this.listener = window.addEventListener("keydown", this.handleKeyDown);
    }

    if ((activeStory && activeStory.id) !== (prevProps.activeStory && prevProps.activeStory.id)) {
      this.setState({ tagging: false, taggingPosition: null });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  handleClose = () => {
    const { history, modalIsOpen, onClose, setChanging, redirectCloseAction } = this.props;

    window.removeEventListener("keydown", this.handleKeyDown);
    this.listener = null;

    this.setState({ tagging: false, taggingPosition: null });
    setChanging(false);
    if (modalIsOpen && redirectCloseAction) {
      const { location: { pathname } } = history;
      const paths = pathname.split("/");
      switch (paths[1]) {
        case "stories":
          if (paths[2] !== "trend") {
            history.push("/");
          }
          break;
        default:
          break;
      }
    }
    onClose();
  };

  handleKeyDown = event => {
    const { handleSwipeLeft, handleSwipeRight } = this.props;
    const { anotherModalOpen } = this.state;
    if (anotherModalOpen) return;
    switch (event.key) {
      case "ArrowLeft":
        handleSwipeLeft();
        break;
      case "ArrowRight":
        handleSwipeRight();
        break;
      default:
        break;
    }
  };

  handleTaggingToggle = () => {
    this.setState(({ tagging }) => ({ tagging: !tagging, taggingPosition: null }));
  };

  handleTagStart = taggingPosition => {
    this.setState({ taggingPosition });
  };

  handleTagEnd = async user => {
    const { activeStory, onStoryUpdate } = this.props;
    const { taggingPosition } = this.state;

    let options = { photoId: activeStory.id, userId: user.id };

    if (taggingPosition.uuid) {
      options.uuid = taggingPosition.uuid;
    } else {
      options = { ...options, left: taggingPosition.left, top: taggingPosition.top };
    }

    try {
      const response = await client.createPhotoTag(options);

      if (response) {
        onStoryUpdate({
          ...activeStory,
          faces: (activeStory.faces || []).filter(face => face.uuid !== taggingPosition.uuid),
          photoTags: [...activeStory.photoTags, response.photoTag]
        });
      }
    } catch (error) {
      errorService.error(error);
    }

    this.setState({ taggingPosition: null });
  };

  handleTaggingClose = () => {
    this.setState({ taggingPosition: null });
  };

  handleTagDelete = async ({ id: photoTagId }) => {
    const { activeStory, onStoryUpdate } = this.props;

    try {
      await client.deletePhotoTag({ photoId: activeStory.id, photoTagId });
    } catch (error) {
      errorService.error(error);
    }

    onStoryUpdate({
      ...activeStory,
      photoTags: activeStory.photoTags.filter(({ id }) => id !== photoTagId)
    });
  };

  handleAnotherModalOpen = updating => {
    this.setState({ anotherModalOpen: updating });
  };

  render() {
    const {
      hideLinks,
      isTrendsetter,
      language,
      modalIsOpen,
      stories,
      onStoryCheerToggle,
      onDelete,
      activeStory,
      currentUserAnswered,
      onCommentsChange,
      onStoryUpdate,
      onPromptChange,
      noActions,
      landingPage,
      landingPagePrefix,
      landingPages,
      mappedVersion,
      modalStyle,
      preview,
      showProfileLink,
      organization,
      organizationId,
      orgName,
      handlers,
      changing,
      setChanging,
      handleSwipeLeft,
      handleSwipeRight,
      hideShareLink,
      handleMoveToNextStory,
      handleApproveClicked,
      handleKeepAsInternalClicked,
      insideIframe,
      reviewVersion,
      approvedContent,
      onUpdateApprovedForSocial,
      approvedVersion,
      internalVersion,
      className,
      videoStudio,
      setImageSliderSwipe,
      onUpdateMappings,
      onVideoStoryPlay,
      onVideoStoryPause
    } = this.props;
    const { tagging, taggingPosition } = this.state;

    if (!modalIsOpen) {
      return null;
    }

    return (
      <>
        <ChqModalDialog
          className={classnames(
            "gallery-lightbox",
            "gallery-lightbox--Story",
            { "gallery-lightbox-tagging": tagging },
            `${className ? `${className}` : ""}`,
            orgName
          )}
          style={modalStyle}
          entrance="zoomIn"
          onClose={this.handleClose}
        >
          <ModalDialog.Body>
            {(reviewVersion || internalVersion || approvedVersion || mappedVersion) && (
              <AdminLightboxActions
                activeStory={activeStory}
                approvedContent={approvedContent}
                landingPages={landingPages}
                approvedVersion={approvedVersion}
                handleApproveClicked={handleApproveClicked}
                handleKeepAsInternalClicked={handleKeepAsInternalClicked}
                internalVersion={internalVersion}
                mappedVersion={mappedVersion}
                onClose={this.handleClose}
                onPromptChange={onPromptChange}
                onUpdateApprovedForSocial={onUpdateApprovedForSocial}
                onSwipeLeft={handleSwipeLeft}
                onSwipeRight={handleSwipeRight}
                onUpdateMappings={onUpdateMappings}
                reviewVersion={reviewVersion}
                stories={stories}
              />
            )}
            {!currentUserAnswered
              && !((activeStory.externalState === "approved_for_everything" || activeStory.externalState === "linkedin"))
              && (
                <div className="gallery-lightbox__header">
                  <span className="legend">
                    <span className="emoji" role="img" aria-label="Wave">👋</span>
                    {" "}
                    <Trans>Now it&apos;s your turn to share!</Trans>
                  </span>
                </div>
              )}
            <div className="chq-pan--bd-container" ref={this.containerRef} {...handlers}>
              {activeStory && (
                <div className="Story-Image">
                  <LightboxStoryPhoto
                    activeStory={activeStory}
                    containerRef={this.containerRef}
                    hideLinks={hideLinks}
                    language={language}
                    isTrendsetter={isTrendsetter}
                    tagging={tagging}
                    onCommentsChange={onCommentsChange}
                    onTagDelete={this.handleTagDelete}
                    onTagStart={this.handleTagStart}
                    onStoryCheerToggle={onStoryCheerToggle}
                    noActions={noActions}
                    landingPage={landingPage}
                    onStoryUpdate={onStoryUpdate}
                    organization={organization}
                    organizationId={organizationId}
                    orgName={orgName}
                    preview={preview}
                    showProfileLink={showProfileLink}
                    changing={changing}
                    onChangedFinished={() => setChanging(false)}
                    onDelete={onDelete}
                    handleApproveClicked={handleApproveClicked}
                    handleMoveToNextStory={handleMoveToNextStory}
                    reviewVersion={reviewVersion}
                    approvedVersion={approvedVersion}
                    internalVersion={internalVersion}
                    videoStudio={videoStudio}
                    onAnotherModalOpen={this.handleAnotherModalOpen}
                    setImageSliderSwipe={setImageSliderSwipe}
                    onVideoStoryPlay={onVideoStoryPlay}
                    onVideoStoryPause={onVideoStoryPause}
                  />
                </div>
              )}

              {activeStory && !reviewVersion && (
                <LightboxActions
                  activeStory={activeStory}
                  hideShareLink={hideShareLink}
                  insideIframe={insideIframe}
                  onClose={this.handleClose}
                  landingPage={landingPage}
                  landingPagePrefix={landingPagePrefix}
                  organization={organization}
                  organizationId={organizationId}
                  preview={preview}
                  onSwipeLeft={handleSwipeLeft}
                  onSwipeRight={handleSwipeRight}
                  orgName={orgName}
                />
              )}

              {stories.length > 1 && !reviewVersion && (
                <LightboxArrows
                  onSwipeLeft={handleSwipeLeft}
                  onSwipeRight={handleSwipeRight}
                />
              )}
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
        {taggingPosition && ReactDOM.createPortal(
          <PhotoTagging
            onClose={this.handleTaggingClose}
            onTag={this.handleTagEnd}
            position={taggingPosition}
          />,
          document.body
        )}
      </>
    );
  }
}

const LightboxStories = ({
  activeStory,
  onStoryChange,
  stories,
  hideShareLink,
  redirectCloseAction = true,
  onKeepAsInternalClicked,
  onApproveClicked,
  onClose,
  ...props
}) => {
  const history = useHistory();
  const [changing, setChanging] = useState(true);
  const [imageSliderSwipe, setImageSliderSwipe] = useState(false);

  const handlers = useSwipeable({
    onSwipedLeft: () => handleSwipeRight(true),
    onSwipedRight: () => handleSwipeLeft(true),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  const handleSwipeLeft = (isSwipe = false) => {
    const isSingleImage = activeStory.visuals.length === 1;
    const isNotImageSwipe = isMultiImageStory(activeStory) && isSwipe && !imageSliderSwipe;
    if (!isSwipe || isNotImageSwipe || isSingleImage) {
      setChanging(true);
      onStoryChange(stories.findIndex(story => story.id === activeStory.id) - 1);
    }
    setImageSliderSwipe(false);
  };

  const handleSwipeRight = (isSwipe = false) => {
    const isSingleImage = activeStory.visuals.length === 1;
    const isNotImageSwipe = isMultiImageStory(activeStory) && isSwipe && !imageSliderSwipe;
    if (!isSwipe || isNotImageSwipe || isSingleImage) {
      setChanging(true);
      onStoryChange(stories.findIndex(story => story.id === activeStory.id) + 1);
    }
    setImageSliderSwipe(false);
  };

  const handlePromptChange = index => {
    setChanging(true);
    onStoryChange(index - 1);
  };

  const handleKeepAsInternalClicked = story => {
    onKeepAsInternalClicked(story);
    handleMoveToNextStory();
    if (stories.length === 1) {
      onClose();
    }
  };

  const handleApproveClicked = story => {
    onApproveClicked(story);
    handleMoveToNextStory();

    if (stories.length === 1) onClose();
  };

  const handleMoveToNextStory = () => {
    // early return if there's only one story left
    if (stories.length === 1) return;

    const nextStoryIndex = stories.findIndex(story => story.id === activeStory.id) + 1;
    const indexToUse = nextStoryIndex >= stories.length ? 0 : nextStoryIndex;

    setChanging(true);
    onStoryChange(indexToUse);
  };

  return (
    <LightboxStoriesWrapper
      {...props}
      onClose={onClose}
      handleKeepAsInternalClicked={handleKeepAsInternalClicked}
      handleApproveClicked={handleApproveClicked}
      handleMoveToNextStory={handleMoveToNextStory}
      history={history}
      handlers={mobileAndTabletCheck() ? handlers : {}}
      activeStory={activeStory}
      onStoryChange={onStoryChange}
      stories={stories}
      changing={changing}
      setChanging={setChanging}
      handleSwipeLeft={handleSwipeLeft}
      handleSwipeRight={handleSwipeRight}
      hideShareLink={hideShareLink}
      redirectCloseAction={redirectCloseAction}
      onPromptChange={handlePromptChange}
      setImageSliderSwipe={setImageSliderSwipe}
    />
  );
};

export default LightboxStories;
